/**
 * Shared server sync when analytics consent is revoked (cookie, modal, cross-tab).
 * G6 Spec A: empty authToken still PUTs preferences when signedIn (cookie jar XOR).
 */
import type { ApiClient } from '../../shared/api/ApiClient.js';
import { readAnonymousKey } from '../../shared/analytics/analyticsAnonymousStorage.js';
import { readStoredAnalyticsSessionIds } from '../../shared/analytics/analyticsSessionStorage.js';
import { orchestrateAnalyticsConsentRevokeFromClient } from '../../shared/analytics/orchestrateAnalyticsConsentRevokeFromClient.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { buildConsentPreferencesPayload } from './consentPreferencesPayload.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import type { ConsentDecision } from './consentTypes.js';
import { notifyConsentSyncFailed } from './consentSyncNotice.js';
import { resolveConsentStorageTenantCode } from './consentStorageTenant.js';
import { putPlatformConsentPreferences, type ConsentApiRolloutFallback } from './platformConsentApi.js';
import { resolveConsentApiFallbackTenantCode } from './resolveConsentApiFallbackTenantCode.js';
import type { CustomerMembershipItemDTO } from '../account/accountTypes.js';

export interface SyncAnalyticsRevokeInput {
  readonly tenantCode: string;
  /** Browse-context ApiClient — guest orchestration + optional profiling tenant calls. */
  readonly api: ApiClient;
  readonly decision: ConsentDecision;
  readonly anonymousKey: string | null;
  readonly sessionIds: readonly string[];
  readonly authToken: string | null;
  /**
   * G6 Spec A — session after `/me` (or equivalent signed-in proof).
   * When true, empty authToken still runs preferences PUT via cookie credentials.
   */
  readonly signedIn?: boolean;
  /** Cross-tab guest revoke: skip PUT. */
  readonly skipPreferencesPut?: boolean;
  readonly memberships?: readonly CustomerMembershipItemDTO[] | null;
  readonly rollout?: ConsentApiRolloutFallback;
}

function isPreferencesAuthReady(input: SyncAnalyticsRevokeInput): boolean {
  const token = input.authToken;
  if (token !== null && token.length > 0) {
    return true;
  }
  return input.signedIn === true;
}

export async function syncAnalyticsConsentRevoke(
  input: SyncAnalyticsRevokeInput,
): Promise<void> {
  if (input.decision.analytics) {
    return;
  }

  let preferencesPutOk = true;
  const preferencesAuthReady = isPreferencesAuthReady(input);
  const storageTenant = resolveConsentStorageTenantCode(input.tenantCode);
  if (input.skipPreferencesPut !== true && preferencesAuthReady) {
    try {
      const { apiBaseUrl } = loadRuntimeConfig();
      const rollout =
        input.rollout ??
        ({
          api: input.api,
          fallbackTenantCode: resolveConsentApiFallbackTenantCode(
            input.tenantCode,
            input.memberships,
          ),
        } satisfies ConsentApiRolloutFallback);
      await putPlatformConsentPreferences({
        apiBaseUrl,
        body: buildConsentPreferencesPayload(
          storageTenant,
          {
            analytics: input.decision.analytics,
            marketing: input.decision.marketing,
          },
          { anonymousKey: input.anonymousKey },
        ),
        rollout,
      });
    } catch (err: unknown) {
      preferencesPutOk = false;
      customerLogger.warn('consent preferences sync failed', err);
      if (input.signedIn === true) {
        notifyConsentSyncFailed();
      }
    }
  }

  await orchestrateAnalyticsConsentRevokeFromClient({
    tenantCode: input.tenantCode,
    api: input.api,
    sessionIds: input.sessionIds,
    anonymousKey: input.anonymousKey,
    clearLocalAfterSuccess:
      preferencesPutOk ||
      !preferencesAuthReady ||
      input.skipPreferencesPut === true,
  });
}

export function readRevokeContext(tenantCode: string): {
  anonymousKey: string | null;
  sessionIds: readonly string[];
} {
  const storageTenant = resolveConsentStorageTenantCode(tenantCode);
  return {
    anonymousKey: readAnonymousKey(storageTenant),
    sessionIds: [...readStoredAnalyticsSessionIds(tenantCode)],
  };
}
